import {
  Button,
  ButtonList,
  DataTable,
  DropdownButton,
  Page,
  SearchInput,
  SortStatus,
  Text,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type {
  EmailIntegrationFlow,
  EmailIntegrationWithFlows,
  Flow,
  ReadEmailIntegrationsSortKey,
} from 'lib-shared-types-api';
import { CSSProperties, Fragment, useCallback, useState } from 'react';

import { integrationsApi } from 'apis/integrations';
import { appActions } from 'app/app.actions';
import { EmailModal } from 'app/views/layout/views/settings/views/email/components/emailTable/components/emailModal/emailModal';
import { EmailModalType } from 'app/views/layout/views/settings/views/email/components/emailTable/components/emailModal/emailModal.types';
import { FlowModal } from 'app/views/layout/views/settings/views/email/components/emailTable/components/flowModal/flowModal';
import { notificationActions } from 'components/notifications/actions/notification';
import { ToggleEmailAutofillFrom } from 'components/toggleEmailAutofillFrom';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useCurrentUser } from 'utils/user/useCurrentUser';

const fetchDelay = 1000;

/** Represents a component that displays a table with the email addresses. */
export const EmailTable = (): JSX.Element => {
  const [sortStatus, setSortStatus] = useState<SortStatus | null>({
    direction: 'asc',
    sortBy: 'email_address',
  });

  const {
    arg,
    items: emails,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    changeArg,
    nextPage,
    previousPage,
    refetchItems,
  } = usePaginatedEndpoint({
    endpoint: integrationsApi.endpoints.readEmailIntegrations,
    initialArg: {
      sort: { key: 'email_address' },
      limit: 50,
    },
    itemsSelector: ({ data }) => data.emails,
  });
  const [deleteEmail] = integrationsApi.useDeleteEmailIntegrationMutation();

  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const {
    currentRole: { atLeastAdmin },
  } = useCurrentUser();

  const handleSearchToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        ampli.emailSearchOpened();
      } else {
        ampli.emailSearchClosed();

        if (arg?.searchTerm) {
          changeArg({
            searchTerm: '',
          });
        }
      }
    },
    [arg, changeArg],
  );

  const handleSearchSubmit = useCallback(
    (value: string) => {
      changeArg({
        searchTerm: value,
      });

      ampli.emailSearchApplied();
    },
    [changeArg],
  );

  const handleSortChange = useCallback(
    (sortStatus: SortStatus | null) => {
      setSortStatus(sortStatus);
      changeArg({
        sort: sortStatus
          ? {
              key: sortStatus.sortBy as ReadEmailIntegrationsSortKey,
              direction: sortStatus.direction,
            }
          : undefined,
      });
    },
    [changeArg],
  );

  const handleGoToFlow = useCallback(
    (flowId: Flow['id']) => () => {
      ampli.emailOptionsMenuOptionFlowClicked();

      navigate({
        path: '/settings/:channel/flow/:flowId',
        params: { path: { channel: 'email', flowId } },
      });
    },
    [navigate],
  );

  const handleRefetchItems = useCallback(() => {
    // We need to set a delay to be sure the listing endpoint
    // has also been updated before refetching emails
    setTimeout(() => refetchItems(), fetchDelay);
  }, [refetchItems]);

  const handleRenameFlow = useCallback(
    (flow: EmailIntegrationFlow) => async () => {
      ampli.emailOptionsMenuOptionRenameClicked();

      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'settings:flow',
            arg: {
              mode: 'rename',
              flow,
            },
          },
        }),
      );

      if (isConfirmed) {
        handleRefetchItems();
      }
    },
    [dispatch, handleRefetchItems],
  );

  const handleEditEmail = useCallback(
    (type: EmailModalType, email?: EmailIntegrationWithFlows) => async () => {
      if (type === 'duplicate') {
        ampli.emailOptionsMenuOptionDuplicateClicked();
      } else if (type === 'update') {
        ampli.emailOptionsMenuOptionEmailClicked();
      }

      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'settings:email',
            arg: {
              type,
              email,
            },
          },
        }),
      );

      if (isConfirmed && type !== 'create') {
        handleRefetchItems();
      }
    },
    [dispatch, handleRefetchItems],
  );

  const handleGoToAuditLog = useCallback(
    (flowId: Flow['id']) => () => {
      ampli.emailFlowOptionsMenuOptionAuditLogClicked();

      navigate({
        path: '/settings/email/flow/:flowId/history',
        params: { path: { flowId } },
      });
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (emailAddress: EmailIntegrationWithFlows['email_address']) => async () => {
      ampli.emailOptionsMenuOptionDeleteClicked();

      const { isConfirmed, hide } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Are you sure?',
              description: `Are you sure you want to remove the "${emailAddress}" email address?`,
              confirm: {
                label: 'Yes, delete it now',
                async: true,
                asyncLabel: 'Deleting email...',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        deleteEmail({ emailAddress })
          .then(() => {
            dispatch(
              notificationActions.notificationAdd({
                title: 'Email address deleted',
                text: `"${emailAddress}" email address has been deleted`,
                icon: 'i-done',
                className: 'success',
                time: 5000,
              }),
            );

            hide();
            handleRefetchItems();
          })
          .catch((error) => {
            dispatch(
              notificationActions.notificationAdd({
                title: 'An error occurred',
                text: `An error occurred when trying to delete the "${emailAddress}" email address`,
                icon: 'i-exclamation',
                className: 'danger',
              }),
            );

            console.warn(error);
          });
      }
    },
    [deleteEmail, dispatch, handleRefetchItems],
  );

  const isError = asyncStatus === 'rejected';
  const isLoading = asyncStatus === 'pending';
  const dataColumnStyle: CSSProperties = {
    width: '33%',
  };

  return (
    <>
      <Page.Section
        title="All emails"
        controls={
          <>
            <SearchInput
              onToggle={handleSearchToggle}
              onSubmit={handleSearchSubmit}
            />

            {atLeastAdmin && (
              <Button onClick={handleEditEmail('create')}>Add email</Button>
            )}
          </>
        }
        callout={<ToggleEmailAutofillFrom />}
      >
        <DataTable<EmailIntegrationWithFlows, never>
          columns={[
            {
              columnId: 'flow_name',
              label: 'Name',
              selector: ({ flows: [flow] }) => flow?.name || '-',
              sortable: true,
              style: dataColumnStyle,
            },
            {
              columnId: 'email_address',
              label: 'Email address',
              selector: (email) =>
                email.spf_sender_email ? (
                  <span>
                    <Text.Paragraph variant="1">
                      {email.spf_sender_email}
                    </Text.Paragraph>
                    <Text.Paragraph variant="2">
                      {email.email_address}
                    </Text.Paragraph>
                  </span>
                ) : (
                  <Text.Paragraph>{email.email_address}</Text.Paragraph>
                ),
              sortable: true,
              style: dataColumnStyle,
            },
            {
              columnId: 'updated_at',
              label: 'Updated',
              selector: (email) => {
                const [flow] = email.flows;
                const dates = [
                  new Date(email.created_at).getTime(),
                  new Date(email.updated_at ?? 0).getTime(),
                  new Date(flow?.updated_at ?? 0).getTime(),
                ];
                const latestTime = Math.max(...dates);

                return (
                  <Timestamp relative time={new Date(latestTime).toString()} />
                );
              },
              sortable: true,
              style: dataColumnStyle,
            },
            {
              columnId: '',
              selector: (email) => {
                const { flows = [], email_address: emailAddress } = email;
                const [emailFlow] = flows;
                const { id: flowId } = emailFlow || {};

                return (
                  <DropdownButton
                    icon="dots-h"
                    fixed
                    buttonProps={{ variant: 'tertiary' }}
                    noArrow
                    dropdown={
                      <ButtonList>
                        <ButtonList.Item onClick={handleGoToFlow(flowId)}>
                          Edit flow
                        </ButtonList.Item>
                        {atLeastAdmin && (
                          <ButtonList.Item
                            onClick={handleEditEmail('duplicate', email)}
                          >
                            Duplicate
                          </ButtonList.Item>
                        )}
                        <ButtonList.Item
                          onClick={handleEditEmail('update', email)}
                        >
                          Setup
                        </ButtonList.Item>
                        <ButtonList.Item onClick={handleRenameFlow(emailFlow)}>
                          Rename
                        </ButtonList.Item>
                        <ButtonList.Item onClick={handleGoToAuditLog(flowId)}>
                          See audit log
                        </ButtonList.Item>
                        {atLeastAdmin && (
                          <Fragment>
                            <ButtonList.Divider />
                            <ButtonList.Item
                              onClick={handleDelete(emailAddress)}
                            >
                              Delete
                            </ButtonList.Item>
                          </Fragment>
                        )}
                      </ButtonList>
                    }
                    onToggle={(isOpen) =>
                      isOpen && ampli.emailOptionsMenuOpened()
                    }
                  />
                );
              },
              style: { width: '10%' },
            },
          ]}
          data={emails}
          paginationProps={{
            loading: isLoading,
            previousButtonDisabled: !hasPreviousPage,
            nextButtonDisabled: !hasNextPage,
            previousButtonClick: previousPage,
            nextButtonClick: nextPage,
          }}
          sortingProps={{
            sortStatus,
            onSortChange: handleSortChange,
          }}
          tableStatusProps={{
            loading: isLoading,
            loadingFailed: isError,
            onLoadingRetry: refetchItems,
          }}
          tableStyleProps={{
            stickyHeader: true,
            stickyFooter: true,
          }}
        />
      </Page.Section>

      <EmailModal />
      <FlowModal />
    </>
  );
};
